import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { MedplumClient } from '@medplum/core';
import {
  Bundle,
  Consent,
  Invoice,
  Patient,
  ServiceRequest,
  Specimen,
} from '@medplum/fhirtypes';
import { OrderSubState } from '../lims-types';
import {
  OrderSearchParams,
  OrderBundleData,
  orderSearchConfig,
} from '../orders/orderSearchConfig';

export type KanbanSharedParams = Omit<OrderSearchParams, 'subState'>;

export const KANBAN_COLUMN_PAGE_SIZE = 10;

export interface KanbanIncludedData {
  patients: Map<string, Patient>;
  specimens: Map<string, Specimen[]>;
  consents: Map<string, Consent>;
  invoices: Map<string, Invoice>;
  deposits: Map<string, boolean>;
}

const EMPTY_INCLUDED: KanbanIncludedData = {
  patients: new Map(),
  specimens: new Map(),
  consents: new Map(),
  invoices: new Map(),
  deposits: new Map(),
};

function extractIncludedData(bundle: Bundle): KanbanIncludedData {
  const patients = new Map<string, Patient>();
  for (const entry of bundle.entry ?? []) {
    if (
      entry.search?.mode !== 'include' ||
      entry.resource?.resourceType !== 'Patient'
    ) {
      continue;
    }

    const p = entry.resource as Patient;
    if (!p.id) {
      continue;
    }

    patients.set(p.id, p);
  }
  const { specimens, consents, invoices, deposits } =
    (orderSearchConfig.processBundleData?.(bundle) ?? {
      specimens: new Map(),
      consents: new Map(),
      invoices: new Map(),
      deposits: new Map(),
    }) as OrderBundleData;
  return { patients, specimens, consents, invoices, deposits };
}

function mergeIncludedData(
  existing: KanbanIncludedData,
  incoming: KanbanIncludedData
): KanbanIncludedData {
  const patients = new Map(existing.patients);
  for (const [k, v] of incoming.patients) {
    patients.set(k, v);
  }

  const specimens = new Map(existing.specimens);
  for (const [k, v] of incoming.specimens) {
    specimens.set(k, v);
  }

  const consents = new Map(existing.consents);
  for (const [k, v] of incoming.consents) {
    consents.set(k, v);
  }

  const invoices = new Map(existing.invoices);
  for (const [k, v] of incoming.invoices) {
    invoices.set(k, v);
  }

  const deposits = new Map(existing.deposits);
  for (const [k, v] of incoming.deposits) {
    deposits.set(k, v);
  }

  return { patients, specimens, consents, invoices, deposits };
}

// ---------------------------------------------------------------------------
export function useKanbanColumn(
  medplum: MedplumClient,
  subState: OrderSubState,
  sharedParams: KanbanSharedParams
) {
  const [orders, setOrders] = useState<ServiceRequest[]>([]);
  const [includedData, setIncludedData] =
    useState<KanbanIncludedData>(EMPTY_INCLUDED);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const [nextPage, setNextPage] = useState(2);

  const paramsRef = useRef<KanbanSharedParams>(sharedParams);
  const includedRef = useRef<KanbanIncludedData>(EMPTY_INCLUDED);

  useEffect(() => {
    paramsRef.current = sharedParams;
  });
  useEffect(() => {
    includedRef.current = includedData;
  }, [includedData]);

  // ---------------------------------------------------------------------------
  const doFetch = useCallback(
    async (params: KanbanSharedParams, page: number) => {
      const rawQuery = orderSearchConfig.buildSearchQuery(
        { ...params, subState },
        page,
        KANBAN_COLUMN_PAGE_SIZE
      ) as string[][];

      if (!rawQuery.some(([k]) => k === '_total')) {
        rawQuery.push(['_total', 'accurate']);
      }

      const bundle = await medplum.search('ServiceRequest', rawQuery);

      const results = (bundle.entry ?? [])
        .filter((e) => e.search?.mode === 'match')
        .map((e) => e.resource as ServiceRequest);

      return {
        results,
        total: bundle.total ?? 0,
        included: extractIncludedData(bundle),
      };
    },
    [medplum, subState]
  );

  // ---------------------------------------------------------------------------
  const fetchInitial = useCallback(
    async (params: KanbanSharedParams) => {
      setLoading(true);
      setOrders([]);
      setTotalCount(undefined);
      setIncludedData(EMPTY_INCLUDED);
      includedRef.current = EMPTY_INCLUDED;
      try {
        const { results, total, included } = await doFetch(params, 1);
        setOrders(results);
        setTotalCount(total);

        setHasMore(results.length < total);
        if (hasMore) {
          setNextPage(2);
        }
        includedRef.current = included;
        setIncludedData(included);
      } catch (e) {
        console.error(`[KanbanColumn:${subState}]`, e);
      } finally {
        setLoading(false);
      }
    },
    [doFetch, hasMore, subState]
  );

  // ---------------------------------------------------------------------------
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) {
      return;
    }
    setLoadingMore(true);
    try {
      const { results, total, included } = await doFetch(
        paramsRef.current,
        nextPage
      );
      const merged = mergeIncludedData(includedRef.current, included);
      includedRef.current = merged;
      setIncludedData(merged);
      setOrders((prev) => {
        const combined = [...prev, ...results];
        setHasMore(combined.length < total);
        return combined;
      });
      setTotalCount(total);
      setNextPage((p) => p + 1);
    } catch (e) {
      console.error(`[KanbanColumn:${subState}]`, e);
    } finally {
      setLoadingMore(false);
    }
  }, [doFetch, hasMore, loadingMore, nextPage, subState]);

  // ---------------------------------------------------------------------------
  const paramsKey = useMemo(() => JSON.stringify(sharedParams), [sharedParams]);

  useEffect(() => {
    fetchInitial(sharedParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, subState]);

  return {
    orders,
    patients: includedData.patients,
    specimens: includedData.specimens,
    consents: includedData.consents,
    invoices: includedData.invoices,
    deposits: includedData.deposits,
    loading,
    loadingMore,
    hasMore,
    totalCount,
    loadMore,
  };
}

